// src/pages/LoginPage.jsx
import { useState, useEffect, useRef } from 'react';
import { signInUser } from '../lib/authService';
import supabase from '../lib/supabaseClient';
import Button from '../components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { requestNewLink, demoEndedCheck, ENDED_MESSAGE, EXPIRED_LINK_MESSAGE, INVALID_LOGIN_MESSAGE, TURNSTILE_SITE_KEY } from '../lib/demoSignin';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // A returning demo visitor: "Email me a new link", and the notices they can be sent here with (?link=expired from a used one-time link,
  // ?demo=ended when a demo past its end date was signed back out).
  // The demo panel is collapsed behind a small link, so an ordinary customer sees the plain sign-in form. Someone sent here from a used one-time
  // demo link (?link=expired, which only a demo visitor can arrive with) gets it already open, because the notice above it points at its button.
  const [demoOpen, setDemoOpen] = useState(() => new URLSearchParams(location.search).get('link') === 'expired');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkNote, setLinkNote] = useState(null); // { ok, message }
  const linkSent = !!(linkNote && linkNote.ok);      // after a send the panel shrinks to the short confirmation (no explanation, no check, no button)
  const turnstileBox = useRef(null);
  const turnstileToken = useRef('');
  const turnstileWidget = useRef(null);
  const siteKey = TURNSTILE_SITE_KEY();
  const query = new URLSearchParams(location.search);
  const arrivalNote = query.get('demo') === 'ended' ? ENDED_MESSAGE : query.get('link') === 'expired' ? EXPIRED_LINK_MESSAGE : '';

  const returnTo = location.state?.returnTo || '/dashboard';
  // Review-sandbox accounts and prospect demos land on the Control Room (Pulse), not the dashboard. `is_reviewer` is stamped into the login's
  // app_metadata when the account is created (users cannot edit app_metadata), so it is known the instant they
  // sign in. A specific returnTo (they were bounced here from a page) is respected.
  const landingFor = (session) =>
    (returnTo === '/dashboard' && session?.user?.app_metadata?.is_reviewer === true)
      ? (session.user.app_metadata.reviewer_kind === 'prospect_demo' ? '/campaign-management' : '/control-room')
      : returnTo;

  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        // an ended demo is not sent on to the app: it is signed out here with the reason
        if ((await demoEndedCheck(supabase, session)).ended) {
          await supabase.auth.signOut();
          setError(ENDED_MESSAGE);
          return;
        }
        navigate(landingFor(session));
      }
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, returnTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await signInUser(email, password);

      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (signInError.message.includes('Email not confirmed')) {
          setError('Please check your email and click the confirmation link before logging in.');
        } else {
          setError(signInError.message);
        }
        setLoading(false);
        return;
      }

      if (data?.session?.user) {
        setIsRedirecting(true);
        await new Promise(resolve => setTimeout(resolve, 300));

        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && (await demoEndedCheck(supabase, session)).ended) {
          // the demo's 5 days are up: the password still exists until cleanup removes the login, but it does not get anyone in
          await supabase.auth.signOut();
          setError(ENDED_MESSAGE);
          setLoading(false);
          setIsRedirecting(false);
        } else if (session?.user) {
          navigate(landingFor(session), { replace: true });
        } else {
          setError('Login successful but session not established. Please try again.');
          setLoading(false);
          setIsRedirecting(false);
        }
      } else {
        setError('Login failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      // signInUser throws for every refusal from the server. A demo past its end date, and a wrong password, get their own plain explanation.
      if (err?.code === 'DEMO_ENDED') setError(ENDED_MESSAGE);
      else if (/invalid login credentials/i.test(err?.message || '')) setError(INVALID_LOGIN_MESSAGE);
      else setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  // Cloudflare Turnstile guards "Email me a new link" (the server checks the token; a token works once, so the widget is reset after every use).
  useEffect(() => {
    if (!siteKey || !demoOpen || linkSent) return undefined;
    let cancelled = false;
    const draw = () => {
      const el = turnstileBox.current;
      if (cancelled || !el || !window.turnstile || el.childElementCount) return;
      turnstileWidget.current = window.turnstile.render(el, {
        sitekey: siteKey,
        // the panel is narrower than the normal widget (300px) on small phones: use the compact one there
        size: el.clientWidth && el.clientWidth < 300 ? 'compact' : 'normal',
        callback: (t) => { turnstileToken.current = t; },
        'expired-callback': () => { turnstileToken.current = ''; },
        'error-callback': () => { turnstileToken.current = ''; },
      });
    };
    const cleanup = () => { cancelled = true; turnstileToken.current = ''; turnstileWidget.current = null; };   // the box is removed when the panel closes: start clean when it opens again
    if (window.turnstile) { draw(); return cleanup; }
    const sc = document.createElement('script');
    sc.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    sc.async = true;
    sc.onload = draw;
    document.head.appendChild(sc);
    return cleanup;
  }, [siteKey, demoOpen, linkSent]);

  const handleNewLink = async () => {
    setLinkNote(null);
    setLinkBusy(true);
    const r = await requestNewLink(email, turnstileToken.current);
    setLinkNote(r);
    setLinkBusy(false);
    if (!r.ok && siteKey && window.turnstile && turnstileWidget.current != null) { turnstileToken.current = ''; try { window.turnstile.reset(turnstileWidget.current); } catch (_) { /* a widget that is gone is not an error */ } }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* LEFT SIDE - Marketing Panel */}
      <div className="hidden lg:flex w-full lg:w-1/2 bg-gradient-to-br from-[#2B7DE9] to-[#1E5BB8] items-center justify-center p-8 lg:p-16 lg:min-h-screen">
        <div className="max-w-xl w-full space-y-6 lg:space-y-8 text-center">
          {/* Fox Hero Visual */}
          <div className="flex justify-center mb-6 lg:mb-8">
            <img
              src="/assets/fox.png"
              alt="SurFox AI"
              className="w-48 h-48 sm:w-56 sm:h-56 lg:w-80 lg:h-80 object-contain"
              style={{
                filter: 'drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.3))',
                animation: 'gentle-float 6s ease-in-out infinite'
              }}
            />
          </div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight px-4">
            Turn Cold Leads Into Closed Deals
          </h1>

          {/* Supporting Copy */}
          <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed max-w-lg mx-auto px-4">
            SurFox AI automatically re-engages your cold leads with natural SMS conversations,
            qualifying them 24/7 while you focus on closing deals.
          </p>

          {/* Social Proof */}
          <div className="pt-4 lg:pt-6 px-4">
            <div className="inline-block bg-white/10 backdrop-blur-sm rounded-lg px-4 sm:px-6 py-3 sm:py-4 border border-white/20">
              <p className="text-white/95 font-semibold text-sm sm:text-base">
                "SurFox AI is a no-brainer for any sales team"
              </p>
              <p className="text-white/75 text-xs sm:text-sm mt-1">
                — CEO, South Florida Staffing Firm
              </p>
            </div>
          </div>

          {/* Optional CTA */}
          <div className="pt-2 lg:pt-4">
            <a
              href="https://www.getsurfox.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-white/90 hover:text-white transition-colors text-sm font-medium"
            >
              See How It Works
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Login Form */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-6 sm:p-8 lg:p-16 min-h-screen">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 lg:mb-10">
            <a href="https://www.getsurfox.com" className="inline-block">
              <img
                src="/newSurFoxLogo1.png"
                alt="SurFox AI"
                className="h-8 sm:h-10 object-contain"
              />
            </a>
          </div>

          {/* Heading */}
          <div className="mb-6 lg:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Sign in to your account to continue
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 lg:space-y-6">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="w-full h-11 sm:h-12 px-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7DE9] focus:border-transparent transition-all"
                disabled={loading || isRedirecting}
              />
            </div>

            {/* Password Input with Toggle */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="w-full h-11 sm:h-12 px-4 pr-12 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7DE9] focus:border-transparent transition-all"
                  disabled={loading || isRedirecting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  disabled={loading || isRedirecting}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end pt-1">
                <a
                  href="/reset-password"
                  className="text-sm text-[#2B7DE9] hover:text-blue-700 transition-colors hover:underline"
                >
                  Forgot password?
                </a>
              </div>
            </div>

            {/* Notice for someone sent here from a used link */}
            {arrivalNote && !error && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4" role="status" data-testid="login-arrival-note">
                <p className="text-blue-800 text-sm">{arrivalNote}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4" role="alert">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Sign In Button */}
            <Button
              type="submit"
              disabled={loading || isRedirecting}
              className="w-full h-11 sm:h-12 bg-black hover:bg-gray-800 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              {isRedirecting ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Redirecting...
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing In...
                </div>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Small text link: the only thing a normal customer sees of the demo panel. */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setDemoOpen((o) => !o)}
              aria-expanded={demoOpen}
              aria-controls="demo-new-link-panel"
              data-testid="demo-link-toggle"
              className="text-sm text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline transition-colors"
            >
              Have a SurFox AI demo? Lost your link?
            </button>
          </div>

          {/* Returning demo visitor: a fresh one-time link emailed to the demo's own address (the password does not change). Works with no /demo page open. */}
          {demoOpen && (
          <div id="demo-new-link-panel" className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="demo-new-link">
            {linkSent ? (
              <>
                <p className="text-sm text-green-700" role="status" data-testid="demo-new-link-note">
                  {linkNote.message}
                </p>
                <button
                  type="button"
                  onClick={() => setLinkNote(null)}
                  data-testid="demo-new-link-again"
                  className="mt-2 text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2 transition-colors"
                >
                  Send another link
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-900">Have a SurFox AI demo?</p>
                <p className="mt-1 text-sm text-gray-600">
                  Lost the email, or already used the one-time link? Type your demo email above and we will send a fresh link. Your password stays the same: it is in your first email, or use Forgot password.
                </p>
                {siteKey && <div ref={turnstileBox} className="mt-3 flex justify-center" data-testid="demo-new-link-check" />}
                <button
                  type="button"
                  onClick={handleNewLink}
                  disabled={linkBusy || loading || isRedirecting}
                  className="mt-3 w-full h-11 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {linkBusy ? 'Sending...' : 'Email me a new link'}
                </button>
                {linkNote && (
                  <p
                    className={`mt-3 text-sm ${linkNote.ok ? 'text-green-700' : 'text-red-700'}`}
                    role={linkNote.ok ? 'status' : 'alert'}
                    data-testid="demo-new-link-note"
                  >
                    {linkNote.message}
                  </p>
                )}
              </>
            )}
          </div>
          )}

          {/* Footer Link */}
          <div className="mt-6 lg:mt-8 text-center">
            <p className="text-sm text-gray-600">
              New to SurFox AI?{' '}
              <a
                href="https://www.getsurfox.com/#pricing"
                className="font-semibold text-[#2B7DE9] hover:text-blue-700 transition-colors hover:underline"
              >
                Get Started
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Add custom CSS for animation */}
      <style>{`
        @keyframes gentle-float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
